import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import Header from '../../components/ui/Header';
import ArticleHero from './components/ArticleHero';
import ArticleMetadata from './components/ArticleMetadata';
import ArticleSidebar from './components/ArticleSidebar';
import RelatedPosts from './components/RelatedPosts';
import EngagementMetrics from './components/EngagementMetrics';
import { blogService } from '../../services/blogService';
import 'highlight.js/styles/github-dark.css';

const BlogArticleView = () => {
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBlogData();
  }, [id]);

  const fetchBlogData = async () => {
    try {
      setLoading(true);
      const data = await blogService?.getBlogById(id);
      setBlog(data);

      // Increment view count
      await blogService?.incrementPopularity(id);

      // Fetch related posts
      if (data?.category) {
        const related = await blogService?.getRelatedBlogs(id, data?.category, 3);
        setRelatedPosts(related || []);
      }
    } catch (err) {
      console.error('Error fetching blog:', err);
      setError(err?.message || 'Failed to load blog post');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading article...</p>
          </div>
        </div>
      </>
    );
  }

  if (error || !blog) {
    return (
      <>
        <Header />
        <div className="min-h-screen flex items-center justify-center bg-background px-4">
          <div className="text-center max-w-md">
            <h2 className="text-2xl font-bold text-foreground mb-2">Article Not Found</h2>
            <p className="text-muted-foreground mb-6">{error || 'The article you are looking for does not exist.'}</p>
            <Link to="/blog-posts" className="text-accent hover:underline">Back to Blog</Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{blog?.title} - Portfolio Blog</title>
        <meta name="description" content={blog?.excerpt} />
      </Helmet>
      <Header />
      <article className="min-h-screen bg-background">
        {/* Hero Section with Featured Image */}
        <ArticleHero blog={blog} />

        {/* Main Content Area */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Main Article Content */}
            <div className="lg:col-span-8">
              {/* Metadata Section */}
              <ArticleMetadata blog={blog} />

              {/* Article Excerpt */}
              <p className="text-xl text-muted-foreground mb-8 leading-relaxed border-l-4 border-accent pl-6 italic">
                {blog?.excerpt}
              </p>

              {/* Markdown Content */}
              <div className="prose prose-lg max-w-none prose-headings:text-foreground prose-p:text-foreground prose-strong:text-foreground prose-a:text-accent prose-code:text-accent prose-pre:bg-slate-900 prose-pre:text-slate-100">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeHighlight]}
                >
                  {blog?.content}
                </ReactMarkdown>
              </div>

              {/* Engagement Metrics */}
              <EngagementMetrics blog={blog} />
            </div>

            {/* Sidebar */}
            <aside className="lg:col-span-4">
              <ArticleSidebar blog={blog} />
            </aside>
          </div>

          {/* Related Posts Section */}
          {relatedPosts?.length > 0 && (
            <RelatedPosts posts={relatedPosts} currentCategory={blog?.category} />
          )}
        </div>
      </article>
    </>
  );
};

export default BlogArticleView;